import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import PublicBottomNav from "../components/PublicBottomNav";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { 
  MapPin, 
  Mountain, 
  Home,
  Users,
  Heart,
  Leaf,
  Sun,
  Coffee,
  ChevronRight,
  HelpCircle,
  Mic,
  MessageCircle
} from "lucide-react";

import JsonLd from "../components/JsonLd";
import OfficialAwardBadge from "../components/OfficialAwardBadge";
import { pic, picSrc } from "../lib/pic";
import { useWide } from "../lib/useWide";

const AboutPage = () => {
  const wide = useWide();
  const aboutSchemaData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "url": "https://www.savicoliving.com/about",
    "mainEntity": {
      "@type": "Person",
      "name": "Steve Haynes",
      "jobTitle": "Founder of Savi Coliving",
      "description": "Twenty years in hospitality — kitchens, restaurant openings, hotel launches, retreat centres, and coliving. Worldpackers SDG Award winner 2025.",
      "url": "https://www.savicoliving.com/about",
      "sameAs": ["https://thecolivingadvisor.com"],
      "award": "Worldpackers Sustainable Development Goals Award 2025",
      "image": "https://customer-assets.emergentagent.com/job_savi-community-1/artifacts/g8yzc0zl_IMG_20250708_133040-EDIT.jpg",
      "worksFor": { "@type": "Organization", "name": "Savi Coliving", "url": "https://www.savicoliving.com" }
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>About Savi | Our Story, Philosophy & Community</title>
        <meta name="description" content="About Savi Coliving. Founded by Steve Haynes — a sober, intentional community for founders and creatives in Valencia, Spain." />
        <meta property="og:title" content="About Savi Coliving" />
        <meta property="og:description" content="A sober, intentional community founded by Steve Haynes. Our philosophy: What you focus on, will expand." />
        <meta property="og:image" content="https://static.wixstatic.com/media/f539fd_c4b11860452a4428b30b62eba4541af0~mv2.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.savicoliving.com/about" />
      </Helmet>
      <JsonLd id="about" data={aboutSchemaData} />
      {/* Navigation */}
      <PublicNav />

      {/* Mobile Navigation */}
      <PublicBottomNav />

      {/* Hero */}
      <header className="relative pt-16">
        <div className="h-[40vh] sm:h-[50vh] relative overflow-hidden">
          <img
            {...pic("hero-villa")}
            alt=""
            fetchpriority="high"
            className="sm:hidden absolute inset-0 w-full h-full object-cover"
          />
          {wide && (
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={picSrc("hero-villa", 960)}
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="https://video.wixstatic.com/video/f539fd_dd46caed36484dc99c7b55ef39a38ba2/1080p/mp4/file.mp4" type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#FBF7F1] from-15% via-[#FBF7F1]/75 via-50% to-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2E2A26] mb-2">About Savi</h1>
              <p className="text-[#2E2A26]/70 text-sm sm:text-base max-w-2xl">
                An intentional community in the mountains of Valencia
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Our Story */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <p className="text-[#C85A38] font-medium mb-2 text-sm sm:text-base">Our Story</p>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#2E2A26]">How Savi Began</h2>
          </div>
          
          <div className="prose prose-lg max-w-none text-[#2E2A26]/80">
            <p className="text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">
              Savi began at the end of twenty years in hospitality — kitchens, restaurant openings, hotel launches, and retreat centres. Steve had spent most of his career running other people's spaces, watching what worked, what fell apart, and what only a long-term operator ever notices.
            </p>
            <p className="text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">
              He kept seeing the same gap in coliving: beautiful properties with no real point of view. A pool, a pretty terrace, a vague nod to "community" — but no standards, no screening, and a house culture left to chance.
            </p>
            <p className="text-sm sm:text-base leading-relaxed">
              When this villa in the Sierra Calderona presented itself — 25 minutes from Valencia, with mountains, light, and silence — Steve saw the chance to build the place he wished he could send people to. Sober by design. Intentional in who lives here. Built for focused people, not for a feed.
            </p>
          </div>
        </div>
      </section>

      {/* The Enso */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-8 bg-[#F4ECE2]" data-testid="enso-section">
        <div className="max-w-3xl mx-auto text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png"
            alt="The Savi enso mark"
            className="h-20 sm:h-24 w-auto mx-auto mb-8"
            loading="lazy"
            decoding="async"
          />
          <p className="font-label text-[#C85A38] text-xs tracking-[0.3em] uppercase mb-6">The Enso</p>
          <p className="font-serif italic text-2xl sm:text-3xl text-[#2E2A26] leading-snug mb-6">
            "Drawn in a single, unhurried stroke — and never quite closed."
          </p>
          <p className="text-[#2E2A26]/70 text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
            Our mark is an enso — the Zen circle made in one breath. It stands for a clear mind, presence, and a day's work finished well. And the gap in the circle? That's intentional. It's the door left open: the space we keep for whoever arrives next, ready to build.
          </p>
        </div>
      </section>

      {/* Savi in numbers: facts on the record for people and machines */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8" data-testid="about-numbers">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <OfficialAwardBadge testid="about-award-badge" width={300} plate={false} className="-ml-4" />
          </div>
          <p className="text-[#C85A38] font-medium mb-2 text-sm sm:text-base">On the record</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-2">Savi in numbers</h2>
          <p className="text-[#2E2A26]/60 text-sm sm:text-base mb-8 max-w-2xl">Europe's award-winning sober coliving. Best for alcohol-free living, deep work and long stays in the mountains near Valencia. The facts, so nobody has to guess.</p>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {[
              ["July 2025", "Opened"],
              ["16", "Rooms in one villa"],
              ["95–98%", "Occupancy since opening"],
              ["25 min", "From Valencia"],
              ["0", "Paid ads, booking platforms or discounts"],
              ["1 month+", "Minimum stay, application only"],
              ["2", "Awards: Best Destination Coliving 2026 · Worldpackers SDG Award 2025"],
              ["3", "Resident dogs"],
            ].map(([value, label]) => (
              <div key={label} className="bg-[#F4ECE2] rounded-xl p-4 sm:p-5 border border-stone-100">
                <dt className="font-serif text-2xl sm:text-3xl text-[#2E2A26] leading-none mb-2">{value}</dt>
                <dd className="text-[#2E2A26]/60 text-xs sm:text-sm leading-snug">{label}</dd>
              </div>
            ))}
          </dl>
          <p className="text-[#2E2A26]/50 text-xs mt-4">Alcohol and drug free for guests and staff since day one. Sierra Calderona, Náquera 46119, Spain.</p>
        </div>
      </section>



      {/* Meet Steve */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-[#E8DAC8]">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">
            <div className="order-2 md:order-1">
              <p className="text-[#C85A38] font-medium mb-2 text-sm sm:text-base">Your Host</p>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-4">Meet Steve</h2>
              <p className="text-[#2E2A26]/70 mb-4 text-sm sm:text-base leading-relaxed">
                Steve is the founder of Savi Coliving. Twenty years in hospitality — kitchens, restaurant openings, hotel launches, retreat centres, and now coliving. Worldpackers SDG Award winner 2025.
              </p>
              <p className="text-[#2E2A26]/70 mb-4 text-sm sm:text-base leading-relaxed">
                At Savi, he handles everything from guest onboarding to sunset barbecues — your host, your guide, and the one making sure the dogs get their morning walk.
              </p>
              <p className="text-[#2E2A26]/70 mb-6 text-sm sm:text-base leading-relaxed">
                Outside Savi, Steve also advises property owners, investors, and retreat founders on premium coliving and lifestyle hospitality projects through <a href="https://thecolivingadvisor.com" target="_blank" rel="noopener noreferrer" className="text-[#C85A38] hover:text-[#A84830] underline underline-offset-2">The Coliving Advisor</a>.
              </p>
              <a href="https://wa.me/34632628051" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-full text-sm">
                  Chat with Steve on WhatsApp
                </Button>
              </a>
            </div>
            <div className="order-1 md:order-2">
              <img loading="lazy" decoding="async" 
                {...pic("steve", { sizes: "(min-width: 768px) 50vw, 100vw", fallback: 768 })}
                alt="Steve"
                className="rounded-2xl sm:rounded-3xl shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <p className="text-[#C85A38] font-medium mb-2 text-sm sm:text-base">Our Values</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26]">What We Believe In</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-[#FBF7F1] rounded-2xl p-5 sm:p-6 shadow-sm">
              <Heart className="w-8 h-8 text-[#C85A38] mb-3" />
              <h3 className="font-serif text-lg sm:text-xl text-[#2E2A26] mb-2">Intentional Living</h3>
              <p className="text-[#2E2A26]/60 text-sm">
                Every space, meal, and moment at Savi is designed to support presence, creativity, and meaningful connection.
              </p>
            </div>
            <div className="bg-[#FBF7F1] rounded-2xl p-5 sm:p-6 shadow-sm">
              <Leaf className="w-8 h-8 text-[#8C9A7C] mb-3" />
              <h3 className="font-serif text-lg sm:text-xl text-[#2E2A26] mb-2">Sober Environment</h3>
              <p className="text-[#2E2A26]/60 text-sm">
                Savi is a 100% sober space. No alcohol or recreational drugs. Just clarity, focus, and authentic connection.
              </p>
            </div>
            <div className="bg-[#FBF7F1] rounded-2xl p-5 sm:p-6 shadow-sm">
              <Users className="w-8 h-8 text-[#2E2A26] mb-3" />
              <h3 className="font-serif text-lg sm:text-xl text-[#2E2A26] mb-2">Community First</h3>
              <p className="text-[#2E2A26]/60 text-sm">
                Shared meals, workshops, and rituals create bonds that last far beyond your stay. We're a family, not just housemates.
              </p>
            </div>
            <div className="bg-[#FBF7F1] rounded-2xl p-5 sm:p-6 shadow-sm">
              <Sun className="w-8 h-8 text-[#C85A38] mb-3" />
              <h3 className="font-serif text-lg sm:text-xl text-[#2E2A26] mb-2">Nature & Wellness</h3>
              <p className="text-[#2E2A26]/60 text-sm">
                From mountain hikes to yoga on the terrace, Savi integrates nature and wellbeing into everyday life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Welcome */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-[#E8DAC8]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#C85A38] font-medium mb-2 text-sm sm:text-base">Our Community</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6 sm:mb-8">Who We Welcome</h2>
          
          <div className="grid sm:grid-cols-2 gap-4 text-left">
            <div className="bg-[#FBF7F1] rounded-2xl p-5 sm:p-6 shadow-sm">
              <h3 className="text-[#2E2A26] font-medium mb-2 text-sm sm:text-base">Digital Nomads & Remote Workers</h3>
              <p className="text-[#2E2A26]/60 text-xs sm:text-sm">Looking for fast WiFi, a quiet workspace, and a community that gets it.</p>
            </div>
            <div className="bg-[#FBF7F1] rounded-2xl p-5 sm:p-6 shadow-sm">
              <h3 className="text-[#2E2A26] font-medium mb-2 text-sm sm:text-base">Creatives & Entrepreneurs</h3>
              <p className="text-[#2E2A26]/60 text-xs sm:text-sm">Artists, writers, founders — anyone who thrives in inspiring spaces.</p>
            </div>
            <div className="bg-[#FBF7F1] rounded-2xl p-5 sm:p-6 shadow-sm">
              <h3 className="text-[#2E2A26] font-medium mb-2 text-sm sm:text-base">Mindful Living Seekers</h3>
              <p className="text-[#2E2A26]/60 text-xs sm:text-sm">Those drawn to sober living, wellness, and intentional community.</p>
            </div>
            <div className="bg-[#FBF7F1] rounded-2xl p-5 sm:p-6 shadow-sm">
              <h3 className="text-[#2E2A26] font-medium mb-2 text-sm sm:text-base">Pet-Friendly Travelers</h3>
              <p className="text-[#2E2A26]/60 text-xs sm:text-sm">We welcome dogs (and the occasional adventurous cat). The site is fully fenced, so they can roam. Main house and kitchen are off limits to animals.</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Dogs */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-[#C85A38] font-medium mb-2 text-sm sm:text-base">Meet the Pack</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-4">Our Four-Legged Family</h2>
            <p className="text-[#2E2A26]/70 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
              Viki, Lluna, and Jenny are the unofficial mascots of Savi. They'll greet you at the door, join you on hikes, and remind you to take breaks from work. All three are friendly, well-trained, and absolutely love guests.
            </p>
          </div>
          
          <div className="grid grid-cols-3 gap-3 sm:gap-6">
            <div className="text-center">
              <img loading="lazy" decoding="async" 
                src="https://customer-assets.emergentagent.com/job_15d85771-4e10-4818-a310-eeba142d0802/artifacts/tdatr56p_Viki.jpg"
                alt="Viki"
                className="rounded-2xl shadow-lg w-full aspect-square object-cover mb-3"
              />
              <h3 className="font-serif text-lg text-[#2E2A26]">Viki</h3>
              <p className="text-[#2E2A26]/60 text-xs sm:text-sm">Chief Greeting Officer</p>
            </div>
            <div className="text-center">
              <img loading="lazy" decoding="async" 
                src="https://customer-assets.emergentagent.com/job_15d85771-4e10-4818-a310-eeba142d0802/artifacts/8quno680_LLuna.jpg"
                alt="Lluna"
                className="rounded-2xl shadow-lg w-full aspect-square object-cover mb-3"
              />
              <h3 className="font-serif text-lg text-[#2E2A26]">Lluna</h3>
              <p className="text-[#2E2A26]/60 text-xs sm:text-sm">Professional Yoga Assistant</p>
            </div>
            <div className="text-center">
              <img loading="lazy" decoding="async" 
                src="https://customer-assets.emergentagent.com/job_15d85771-4e10-4818-a310-eeba142d0802/artifacts/n0c0tva9_Jenny.jpg"
                alt="Jenny"
                className="rounded-2xl shadow-lg w-full aspect-square object-cover mb-3"
              />
              <h3 className="font-serif text-lg text-[#2E2A26]">Jenny</h3>
              <p className="text-[#2E2A26]/60 text-xs sm:text-sm">Head of Security</p>
            </div>
          </div>
          
          <p className="text-center text-[#2E2A26]/60 text-sm mt-6">
            🐾 Savi is a pet-friendly community — you're welcome to bring your furry companion too. Fully fenced grounds; main house and kitchen are animal-free.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-[#E8DAC8]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-3 sm:mb-4">Think You'd Be a Good Fit?</h2>
          <p className="text-[#2E2A26]/60 mb-6 text-sm sm:text-base">
            We keep things intentional. Apply and tell us about yourself.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/rooms">
              <Button className="bg-[#C85A38] hover:bg-[#D06A4F] text-white rounded-full px-6 sm:px-8 w-full sm:w-auto">
                View Rooms
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-[#2E2A26] text-[#2E2A26] rounded-full px-6 sm:px-8 w-full sm:w-auto">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default AboutPage;
